import React, { useEffect, useState } from 'react';
import { sb } from '../../supabase';
import { CATEGORIES } from '../../lib/supplierDashboardConstants';
import { REQUEST_UNITS } from '../../lib/requestFormOptions';

// Admin edit of a trader's request (owner, 2026-10-12): "a trader uploads a
// request without photos or a category - the admin must be able to fix it".
//
// There is no admin UPDATE policy on public.requests, so a plain .update()
// would match zero rows and report success. Every save goes through
// admin_update_request (20261012202033), which edits a fixed list of fields,
// records the change in request_admin_edits, masks contact details through
// the same trigger as the trader's own save, and re-runs the translation when
// the Arabic title or description changed.
//
// Images: uploaded to request-attachments/admin-uploads/<request id>/ (the
// only folder the admin upload policy allows), then the full attachment list
// is sent to the RPC, which accepts only paths the request already had or
// paths under that folder.
//
// ⚠️ Suppliers see attachments (rfq-attachment-url, CLAUDE.md security rule
// 6): an image added here must not carry a phone number, WeChat ID or shop
// name. Nothing screens it.

const IMAGE_RE = /\.(jpe?g|png|webp|gif|heic)$/i;
const safeName = (n) => String(n || 'file').replace(/[^A-Za-z0-9._-]+/g, '_').slice(-80);
const fileName = (p) => String(p).split('/').pop();

export default function RequestEditPanel({ request, signed = {}, isAr, flash, onSaved }) {
  const r = request;
  const initial = () => ({
    title_ar: r.title_ar || '',
    description_ar: r.description_ar || r.description || '',
    category: r.category || '',
    quantity: r.quantity || '',
    unit: r.unit || '',
    budget_per_unit: r.budget_per_unit ?? '',
    budget_currency: r.budget_currency || 'SAR',
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [paths, setPaths] = useState(r.attachment_paths || []);
  const [newFiles, setNewFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setForm(initial()); setPaths(r.attachment_paths || []); setNewFiles([]); setErr(''); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [r.id, r.title_ar, r.description_ar, r.category, r.quantity, r.unit, r.budget_per_unit, r.budget_currency, (r.attachment_paths || []).join('|')]);

  const cats = (CATEGORIES[isAr ? 'ar' : 'en'] || []).filter((c) => c.val !== 'all');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setErr('');
    if (!form.title_ar.trim()) { setErr(isAr ? 'العنوان مطلوب.' : 'Title is required.'); return; }
    setBusy(true);
    try {
      // 1) upload new files first; a failed upload stops the save
      const uploaded = [];
      for (const f of newFiles) {
        const path = `admin-uploads/${r.id}/${Date.now().toString(36)}_${safeName(f.name)}`;
        const { error: upErr } = await sb.storage.from('request-attachments').upload(path, f, { upsert: false, contentType: f.type || undefined });
        if (upErr) {
          console.error('[RequestEditPanel] upload failed:', upErr.message || upErr);
          throw new Error(isAr ? `تعذّر رفع ${f.name}` : `Could not upload ${f.name}`);
        }
        uploaded.push(path);
      }
      // 2) only the fields that changed
      const base = initial();
      const changes = {};
      Object.keys(form).forEach((k) => {
        if (String(form[k] ?? '') !== String(base[k] ?? '')) changes[k] = form[k] === '' ? null : form[k];
      });
      const nextPaths = [...paths, ...uploaded];
      if (nextPaths.join('|') !== (r.attachment_paths || []).join('|')) changes.attachment_paths = nextPaths;
      if (!Object.keys(changes).length) { setBusy(false); setOpen(false); return; }

      const { data, error } = await sb.rpc('admin_update_request', { p_request_id: r.id, p_changes: changes });
      if (error) {
        console.error('[RequestEditPanel] admin_update_request failed:', error.code || '', error.message || error);
        throw new Error(isAr ? `تعذّر الحفظ: ${error.message}` : `Could not save: ${error.message}`);
      }
      flash?.(data?.retranslating
        ? (isAr ? 'حُفظ التعديل، والترجمة تُعاد الآن.' : 'Saved. Translation is re-running.')
        : (isAr ? 'حُفظ التعديل.' : 'Saved.'));
      setOpen(false);
      onSaved?.();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const label = (t) => <span style={{ display: 'block', fontSize: 12, color: 'var(--ac-faint)', marginBottom: 4 }}>{t}</span>;
  const input = { width: '100%', padding: '8px 10px', border: '1px solid var(--ac-line)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box' };

  if (!open) {
    return (
      <button className="ac-btn ac-btn-sm" onClick={() => setOpen(true)}>
        {isAr ? 'تعديل الطلب' : 'Edit request'}
      </button>
    );
  }

  return (
    <div className="ac-card" style={{ marginTop: 12, padding: 16 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{isAr ? 'تعديل الطلب' : 'Edit request'}</h3>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--ac-faint)' }}>
        {isAr
          ? 'كل تعديل يُسجَّل باسمك. تعديل العنوان أو الوصف يعيد الترجمة للإنجليزي والصيني. أرقام التواصل تُخفى تلقائياً من النص — لكن ليس من الصور: لا ترفع صورة فيها رقم أو حساب تواصل.'
          : 'Every edit is recorded under your name. Changing the title or description re-runs the English and Chinese translation. Contact details are masked from text automatically - not from images: do not upload an image showing a number or account.'}
      </p>

      <div style={{ display: 'grid', gap: 12 }}>
        <label>{label(isAr ? 'العنوان (عربي)' : 'Title (Arabic)')}
          <input style={input} dir="rtl" value={form.title_ar} onChange={set('title_ar')} /></label>
        <label>{label(isAr ? 'الوصف (عربي)' : 'Description (Arabic)')}
          <textarea style={{ ...input, minHeight: 110 }} dir="rtl" value={form.description_ar} onChange={set('description_ar')} /></label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <label>{label(isAr ? 'الفئة' : 'Category')}
            <select style={input} value={form.category} onChange={set('category')}>
              <option value="">{isAr ? '— بدون فئة —' : '— none —'}</option>
              {cats.map((c) => <option key={c.val} value={c.val}>{c.label}</option>)}
              {form.category && !cats.some((c) => c.val === form.category) && <option value={form.category}>{form.category}</option>}
            </select></label>
          <label>{label(isAr ? 'الكمية' : 'Quantity')}
            <input style={input} inputMode="numeric" value={form.quantity} onChange={set('quantity')} /></label>
          <label>{label(isAr ? 'الوحدة' : 'Unit')}
            <select style={input} value={form.unit} onChange={set('unit')}>
              <option value="">—</option>
              {REQUEST_UNITS.map((u) => <option key={u.key} value={u.key}>{isAr ? u.ar : u.en}</option>)}
            </select></label>
          <label>{label(isAr ? 'السعر المستهدف للوحدة' : 'Target price per unit')}
            <input style={input} inputMode="decimal" value={form.budget_per_unit} onChange={set('budget_per_unit')} /></label>
          <label>{label(isAr ? 'العملة' : 'Currency')}
            <select style={input} value={form.budget_currency} onChange={set('budget_currency')}>
              {['SAR', 'USD', 'CNY'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
        </div>

        <div>
          {label(isAr ? 'الصور والملفات' : 'Images and files')}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            {paths.map((p) => (
              <div key={p} style={{ position: 'relative', width: 96 }}>
                {signed[p] && IMAGE_RE.test(p)
                  ? <img src={signed[p]} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--ac-line)' }} />
                  : <div style={{ width: 96, height: 96, borderRadius: 10, border: '1px solid var(--ac-line)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 4, boxSizing: 'border-box', overflow: 'hidden' }}>{fileName(p)}</div>}
                <button className="ac-btn ac-btn-sm" style={{ width: '100%', marginTop: 4 }}
                  onClick={() => setPaths((ps) => ps.filter((x) => x !== p))}>
                  {isAr ? 'حذف' : 'Remove'}
                </button>
              </div>
            ))}
            {newFiles.map((f, i) => (
              <div key={`new-${i}`} style={{ width: 96 }}>
                <div style={{ width: 96, height: 96, borderRadius: 10, border: '1px dashed var(--ac-line)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 4, boxSizing: 'border-box', overflow: 'hidden' }}>
                  {isAr ? 'جديد: ' : 'New: '}{f.name}
                </div>
                <button className="ac-btn ac-btn-sm" style={{ width: '100%', marginTop: 4 }}
                  onClick={() => setNewFiles((fs) => fs.filter((_, j) => j !== i))}>
                  {isAr ? 'إلغاء' : 'Cancel'}
                </button>
              </div>
            ))}
          </div>
          <input type="file" multiple accept="image/*,application/pdf"
            onChange={(e) => { const fs = Array.from(e.target.files || []); setNewFiles((x) => [...x, ...fs]); e.target.value = ''; }} />
        </div>
      </div>

      {err && <p style={{ color: 'var(--ac-danger)', fontSize: 13, margin: '12px 0 0' }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="ac-btn ac-btn-primary" disabled={busy} onClick={save}>
          {busy ? (isAr ? 'جارٍ الحفظ…' : 'Saving…') : (isAr ? 'حفظ التعديل' : 'Save changes')}
        </button>
        <button className="ac-btn" disabled={busy} onClick={() => { setOpen(false); setForm(initial()); setPaths(r.attachment_paths || []); setNewFiles([]); setErr(''); }}>
          {isAr ? 'إلغاء' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
